'use client';

import { AnimatePresence, motion } from 'framer-motion';
import type { ReactNode} from 'react';
import { useState } from 'react';
import styled from 'styled-components';

import { ArrowUpSmall } from '@zoomit/icons';

import type { SpacingType } from '../../css/spacing';
import { spacingHelper } from '../../css/spacingOld';

type CollapsableProps = {
  title: string | ReactNode;
  children?: ReactNode;
  defaultOpen?: boolean;
  headSpacing?: SpacingType;
  bodySpacing?: SpacingType;
  onCollapse?: (isOpen: boolean) => void;
  hiddenArrow?: boolean;
};

export const Collapsable = (props: CollapsableProps) => {
  const {
    title,
    children,
    defaultOpen = true,
    headSpacing = { px: 'x6', py: 'x3' },
    bodySpacing = { px: 'x6', pb: 'x3' },
    onCollapse,
    hiddenArrow,
  } = props;
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const contentVariants = {
    open: { height: 'auto', overflow: 'initial' },
    collapsed: { height: 0, overflow: 'hidden' },
  };

  const handleCollapse = () => {
    setIsOpen(!isOpen);
    onCollapse && onCollapse(!isOpen);
  };

  return (
    <div>
      <CollapseButton
        type="button"
        isOpen={isOpen}
        onClick={handleCollapse}
        className="collapsible fa"
        spacing={headSpacing}
      >
        {title}
        {!hiddenArrow && <ArrowUpSmall className="icon" size="md" />}
      </CollapseButton>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.section
            key="content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={contentVariants}
            transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
          >
            <SectionWrapper spacing={bodySpacing}>{children}</SectionWrapper>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};

type CollapsableButtonProps = {
  isOpen: boolean;
  spacing?: SpacingType;
};

type SectionWrapperProps = {
  spacing?: SpacingType;
};

const CollapseButton = styled.button<CollapsableButtonProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  border: none;
  background: transparent;
  font-family: inherit;
  ${({ spacing }) => spacing && spacingHelper(spacing)};
  font-size: 1.6rem;
  font-weight: bold;
  line-height: 1.69;
  cursor: pointer;
  color: #383e43;

  & .icon {
    transition: transform 0.25s;
    transform: ${({ isOpen }) => (isOpen ? 'rotate(0)' : 'rotate(180deg)')};
  }
`;

const SectionWrapper = styled.div<SectionWrapperProps>`
  ${({ spacing }) => spacing && spacingHelper(spacing)};
`;
